import { type MarkdownToJSX, parser } from "markdown-to-jsx";
import { resolveStepColor } from "../../shared/colors";
import { tryParseDockerfile } from "../../shared/dockerfile";
import {
  isScratchStep,
  parseStepConfig,
  type RouteType,
  stepDirFromPath,
} from "../../shared/stepConfig";
import { isUntouchedReadmeStub } from "./readmeStub";

type CommitFileRow = {
  readonly path: string;
  readonly blobHash: string;
  readonly blob:
    | { readonly hash: string; readonly content: string }
    | undefined;
};

type ConnectorLookup = {
  readonly id: string;
  readonly name: string;
  readonly identifier: string;
};

type StepCounterData = {
  executionCount: number;
  lastStatus: string | null;
  lastKind: string | null;
  lastExecutionId: string | null;
  lastCompletedAt: number | null;
};

export type ParsedStep = {
  id: string;
  name: string;
  canonicalId: string;
  color: string;
  command: string;
  hasOutput: boolean;
  hasStorage: boolean;
  publicRoute: string | null;
  routeExternalId: string | null;
  routeType: RouteType | null;
  pinned: boolean;
  schedule: { cronExpression: string } | null;
  emailAddress: string | null;
  executionCount: number;
  lastStatus: string | null;
  lastKind: string | null;
  lastExecutionId: string | null;
  lastCompletedAt: number | null;
  links: string[];
  connectedApps: {
    connectedApp: ConnectorLookup | null;
    connectedAppId: string;
    slug: string;
    appType: string;
  }[];
  timeoutSeconds: number | null;
  readmeDescription: string | null;
};

function extractReadmeDescription(raw: string): string | null {
  for (const para of raw.split(/\n\s*\n/)) {
    const head = para.trim();
    if (!head) continue;
    if (head.startsWith("#") || head.startsWith("**")) continue;
    return stripInlineMarkdown(head);
  }
  return null;
}

function stripInlineMarkdown(s: string): string {
  return astToText(parser(s)).replace(/\s+/g, " ").trim();
}

function astToText(nodes: MarkdownToJSX.ASTNode[]): string {
  let out = "";
  for (const n of nodes) {
    if ("text" in n && typeof n.text === "string") out += n.text;
    else if ("alt" in n && typeof n.alt === "string") out += n.alt;
    else if ("children" in n && Array.isArray(n.children))
      out += astToText(n.children);
  }
  return out;
}

type ParsedLink = {
  id: string;
  fromStepId: string;
  toStepId: string;
  createdAt: number;
};

export function parseCommitData(
  files: readonly CommitFileRow[],
  connectorsById?: Map<string, ConnectorLookup>,
  stepCounters?: Map<string, StepCounterData>,
  slugToAppId?: Map<string, string>
): {
  steps: ParsedStep[];
  links: ParsedLink[];
  workflowReadme: string | null;
} {
  const stepConfigs = new Map<string, string>();
  const dockerfileByStep = new Map<string, string>();
  const readmeByStep = new Map<string, string>();
  let workflowReadme: string | null = null;

  for (const f of files) {
    if (!f.blob) continue;

    if (f.path === "README.md") {
      workflowReadme = isUntouchedReadmeStub(f) ? null : f.blob.content;
      continue;
    }

    const dir = stepDirFromPath(f.path);
    if (dir && !isScratchStep(dir)) {
      const relativePath = f.path.substring(dir.length + 1);
      if (relativePath === "config.toml") {
        stepConfigs.set(dir, f.blob.content);
      } else if (relativePath === "Dockerfile") {
        dockerfileByStep.set(dir, f.blob.content);
      } else if (relativePath === "README.md") {
        readmeByStep.set(dir, f.blob.content);
      }
    }
  }

  const steps: ParsedStep[] = [];
  const allLinks: ParsedLink[] = [];
  const lookup = connectorsById ?? new Map<string, ConnectorLookup>();

  for (const [dir, configRaw] of stepConfigs) {
    const config = parseStepConfig(configRaw);
    const dockerfile = dockerfileByStep.get(dir);
    const instructions = dockerfile ? tryParseDockerfile(dockerfile) : [];
    const cmd = instructions.find((i) => i.kind === "cmd");
    const command = cmd?.kind === "cmd" ? cmd.command : "";
    const hasStorage = instructions.some((i) => i.kind === "volume");

    const appEntries = config.connectors ?? [];

    steps.push({
      id: dir,
      name: dir,
      canonicalId: dir,
      color: resolveStepColor(config.color, dir),
      command,
      hasOutput: config.output,
      hasStorage,
      publicRoute: config.route ?? null,
      routeExternalId:
        config.routeAuth === "external_id" ? (config.externalId ?? null) : null,
      routeType: config.routeType ?? null,
      pinned: config.pinned ?? false,
      schedule: config.cron ? { cronExpression: config.cron } : null,
      emailAddress: config.emailAddress ?? null,
      executionCount: stepCounters?.get(dir)?.executionCount ?? 0,
      lastStatus: stepCounters?.get(dir)?.lastStatus ?? null,
      lastKind: stepCounters?.get(dir)?.lastKind ?? null,
      lastExecutionId: stepCounters?.get(dir)?.lastExecutionId ?? null,
      lastCompletedAt: stepCounters?.get(dir)?.lastCompletedAt ?? null,
      links: config.links ?? [],
      connectedApps: appEntries.map((entry) => {
        const appId = slugToAppId?.get(entry.name) ?? entry.name;
        return {
          connectedApp: lookup.get(appId) ?? null,
          connectedAppId: appId,
          slug: entry.name,
          appType: entry.type,
        };
      }),
      timeoutSeconds: config.timeout ?? null,
      readmeDescription: extractReadmeDescription(readmeByStep.get(dir) ?? ""),
    });

    for (const target of config.links ?? []) {
      allLinks.push({
        id: `${dir}/${target}`,
        fromStepId: dir,
        toStepId: target,
        createdAt: 0,
      });
    }
  }

  steps.sort((a, b) => a.name.localeCompare(b.name));

  return { steps, links: allLinks, workflowReadme };
}
